import type { ExperimentsGetSpeculativeDataQuery$data } from '../graphQLOperations/__generated__/ExperimentsGetSpeculativeDataQuery.graphql';

export const LOCK_TTL_MS = 60_000;
export const LOCK_HEARTBEAT_INTERVAL_MS = LOCK_TTL_MS / 2;

// Phased heartbeat: fast initial refresh to populate modified_* columns,
// then aggressive interval until warmup period ends, then standard cadence.
export const WARMUP_FIRST_BEAT_MS = 5_000;
export const WARMUP_BEAT_INTERVAL_MS = 15_000;
export const WARMUP_DURATION_MS = 60_000;

export function getHeartbeatDelay(elapsedMs: number): number {
  if (elapsedMs < WARMUP_FIRST_BEAT_MS) {
    return WARMUP_FIRST_BEAT_MS - elapsedMs;
  }
  if (elapsedMs < WARMUP_DURATION_MS) {
    return WARMUP_BEAT_INTERVAL_MS;
  }
  return LOCK_HEARTBEAT_INTERVAL_MS;
}

export const ACTIVE_MODAL_SESSION_VARIANT = 'active_modal_session';

export interface LockData {
  sessionId: string;
  claimedAt: string;
}

export function isLockActive(lockData: LockData | undefined, now: Date = new Date()): boolean {
  if (!lockData?.sessionId || !lockData?.claimedAt) return false;

  try {
    const claimedAt = new Date(lockData.claimedAt);
    const elapsed = now.getTime() - claimedAt.getTime();
    return elapsed < LOCK_TTL_MS;
  } catch {
    return false;
  }
}

export function isLockOwnedBySession(
  lockData: LockData | undefined,
  sessionId: string,
  now: Date = new Date(),
): boolean {
  return isLockActive(lockData, now) && lockData?.sessionId === sessionId;
}

export function isLockHeldByAnotherSession(
  lockData: LockData | undefined,
  sessionId: string,
  now: Date = new Date(),
): boolean {
  return isLockActive(lockData, now) && lockData?.sessionId !== sessionId;
}

export function extractLockData(
  query: ExperimentsGetSpeculativeDataQuery$data | null | undefined,
): LockData | undefined {
  const speculativeData = query?.userByPublicUserId?.experimentsSpeculativeData;
  const lockEntry = speculativeData?.find(
    data => data?.variantName === ACTIVE_MODAL_SESSION_VARIANT && !!data?.payload?.rootElement,
  );

  if (!lockEntry?.payload?.rootElement) return undefined;

  const root = lockEntry.payload.rootElement;
  let payload: LockData;

  try {
    payload = typeof root === 'string' ? JSON.parse(root) : root;
  } catch {
    return undefined;
  }

  if (!payload.sessionId || !payload.claimedAt) return undefined;

  return {
    sessionId: payload.sessionId,
    claimedAt: payload.claimedAt,
  };
}

export function buildLockPayload(sessionId: string): string {
  return JSON.stringify({
    sessionId,
    claimedAt: new Date().toISOString(),
  });
}

// --- localStorage cross-tab lock (same-browser mutex) ---

const BRAIN_BREAK_LOCK_KEY = 'brain_break_active_lock';

export interface LocalLockEntry {
  tabId: string;
  claimedAt: number;
}

export function tryAcquireLocalLock(tabId: string, ttlMs: number = LOCK_TTL_MS): boolean {
  try {
    const existing = readLocalLock();
    if (existing && isLocalLockActive(existing, ttlMs) && existing.tabId !== tabId) {
      return false;
    }
    const entry: LocalLockEntry = { tabId, claimedAt: Date.now() };
    localStorage.setItem(BRAIN_BREAK_LOCK_KEY, JSON.stringify(entry));
    return true;
  } catch {
    console.warn('[BrainBreak] localStorage unavailable, mutex disabled');
    return true;
  }
}

export function isLocalLockHeld(tabId: string, ttlMs: number = LOCK_TTL_MS): boolean {
  try {
    const existing = readLocalLock();
    return !!(existing && isLocalLockActive(existing, ttlMs) && existing.tabId !== tabId);
  } catch {
    return false;
  }
}

export function releaseLocalLock(tabId: string): void {
  try {
    const existing = readLocalLock();
    if (existing?.tabId === tabId) {
      localStorage.removeItem(BRAIN_BREAK_LOCK_KEY);
    }
  } catch {
    // Silently ignore - localStorage may be unavailable
  }
}

function readLocalLock(): LocalLockEntry | undefined {
  try {
    const raw = localStorage.getItem(BRAIN_BREAK_LOCK_KEY);
    if (!raw) return undefined;
    return JSON.parse(raw) as LocalLockEntry;
  } catch {
    return undefined;
  }
}

function isLocalLockActive(entry: LocalLockEntry, ttlMs: number): boolean {
  return Date.now() - entry.claimedAt < ttlMs;
}
